import { SaveOutlined, ShareAltOutlined, CopyOutlined } from '@ant-design/icons';
import './index.scss';
import { useAppSelector } from '@/store/hooks';
import { CanvasIcon } from '@/common/svg/canvas';
import { Progress, message, Dropdown, type MenuProps } from 'antd';
import { useState, useMemo, useCallback, useRef, forwardRef } from 'react';
import { MoreOptionsIcon } from '@/common/svg/more-options';
import { AssignIcon } from '@/common/svg/assign';
import { VisualizationFullscreenToggleIcon } from '@/common/svg/visualization-fullscreen-toggle';
import { EyeOffIcon } from '@/common/svg/eye-off';
import { useTheme } from '@/contexts/ThemeContext';
import {
  ShareVisualizationModal,
  SaveTemplateModal,
  ExportPdfModal,
  AssignModal,
} from './popup-modals';
import { withVisualizationBoundaryStyles } from './visualization-boundary-styles';

function VisualizationPanel({
  onCollapse,
  iframeRenderKey,
}: {
  onCollapse?: () => void;
  iframeRenderKey?: number;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const handleToggleFullscreen = useCallback(async () => {
    try {
      const iframe = iframeRef.current;
      if (!iframe) return;

      if (document.fullscreenElement === iframe) {
        await document.exitFullscreen();
        return;
      }

      await iframe.requestFullscreen();
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
      message.error(
        'Unable to switch fullscreen mode. This may be blocked by your browser settings.',
      );
    }
  }, []);

  return (
    <div className="visualization-panel">
      <VisualizationPanelHeader
        onToggleFullscreen={handleToggleFullscreen}
        onCollapse={onCollapse}
      />
      <VisualizationPanelBody ref={iframeRef} iframeRenderKey={iframeRenderKey} />
    </div>
  );
}

function VisualizationPanelHeader({
  onToggleFullscreen,
  onCollapse,
}: {
  onToggleFullscreen: () => void;
  onCollapse?: () => void;
}) {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const { visualization: visualizationState } = useAppSelector((state) => state.canvas);

  const { theme } = useTheme();

  const handleShareButtonClick = useCallback(() => {
    if (visualizationState.sourceCode) {
      setIsShareModalOpen(true);
    } else {
      message.warning('No plan to share yet');
    }
  }, [visualizationState.sourceCode]);

  const handleExportButtonClick = useCallback(() => {
    if (!visualizationState.sourceCode) {
      message.warning('No plan to export yet');
      return;
    }
    setIsExportPdfModalOpen(true);
  }, [visualizationState.sourceCode]);

  const handleSaveTemplateButtonClick = useCallback(() => {
    setIsSaveTemplateModalOpen(true);
  }, []);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    if (visualizationState.sourceCode) {
      items.push({
        key: 'save-template',
        label: 'Save as Template',
        icon: <SaveOutlined />,
        onClick: handleSaveTemplateButtonClick,
      });
    }

    items.push(
      {
        key: 'share',
        label: 'Publish & Share',
        icon: <ShareAltOutlined />,
        onClick: handleShareButtonClick,
      },
      {
        key: 'download',
        label: 'Download',
        icon: <CopyOutlined />,
        onClick: handleExportButtonClick,
      },
    );

    return items;
  }, [
    visualizationState.sourceCode,
    handleShareButtonClick,
    handleExportButtonClick,
    handleSaveTemplateButtonClick,
  ]);

  return (
    <div className="visualization-panel-header">
      <div className="visualization-panel-header-right">
        <button
          type="button"
          className="visualization-panel-header-assign-button"
          aria-label="Assign"
          title="Assign"
          onClick={() => setIsAssignModalOpen(true)}
        >
          <AssignIcon fill={theme === 'dark' ? '#fff' : '#000'} />
          <span>Assign</span>
        </button>
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement="bottomRight"
          overlayClassName="visualization-panel-more-dropdown"
        >
          <button
            className="visualization-panel-header-more-button"
            aria-label="More options"
            title="More options"
          >
            <MoreOptionsIcon fill={theme === 'dark' ? '#fff' : '#000'} />
          </button>
        </Dropdown>
        <button
          type="button"
          className="visualization-panel-header-fullscreen-button"
          aria-label="Toggle fullscreen"
          title="Toggle fullscreen"
          onClick={onToggleFullscreen}
        >
          <VisualizationFullscreenToggleIcon stroke={theme === 'dark' ? '#C0C0C0' : '#5f6872'} />
        </button>
        {onCollapse && (
          <button
            type="button"
            className="visualization-panel-header-collapse-button"
            aria-label="Collapse visualization panel"
            title="Collapse visualization panel"
            onClick={onCollapse}
          >
            <EyeOffIcon stroke={theme === 'dark' ? '#C0C0C0' : '#5f6872'} width={18} height={18} />
          </button>
        )}
      </div>
      <ShareVisualizationModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
      <SaveTemplateModal
        isOpen={isSaveTemplateModalOpen}
        onClose={() => setIsSaveTemplateModalOpen(false)}
      />
      <ExportPdfModal
        isOpen={isExportPdfModalOpen}
        onClose={() => setIsExportPdfModalOpen(false)}
      />
      <AssignModal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} />
    </div>
  );
}

const VisualizationPanelBody = forwardRef<HTMLIFrameElement | null, { iframeRenderKey?: number }>(
  function VisualizationPanelBody({ iframeRenderKey }, ref) {
    const { visualization, isLoading } = useAppSelector((state) => state.canvas);
    const sourceCodeWithBoundaryStyles = useMemo(
      () => withVisualizationBoundaryStyles(visualization.sourceCode),
      [visualization.sourceCode],
    );

    if (isLoading) {
      return <VisualizationLoadingPlaceholder />;
    }

    if (!visualization.sourceCode) {
      return <VisualizationEmptyPlaceholder />;
    }

    return (
      <div className="visualization-panel-body">
        <iframe
          key={iframeRenderKey}
          ref={ref}
          srcDoc={sourceCodeWithBoundaryStyles}
          title="Visualization"
          className="visualization-panel-body-iframe"
          sandbox="allow-scripts" // Only allow-scripts for security; visualization is server-generated
        />
      </div>
    );
  },
);

function VisualizationEmptyPlaceholder() {
  return (
    <div className="visualization-empty-placeholder">
      <div className="visualization-empty-placeholder-content">
        <CanvasIcon />
        <p>Your plan will appear here</p>
      </div>
    </div>
  );
}

function VisualizationLoadingPlaceholder() {
  const { progressState } = useAppSelector((state) => state.canvas);

  return (
    <div className="visualization-loading-placeholder">
      <Progress percent={progressState.percent} status={progressState.status} type="circle" />
      <div className="visualization-loading-placeholder-title">
        Hang tight, it might take a few minutes to generate your plan.
      </div>
    </div>
  );
}

export default VisualizationPanel;
